import { useCallback, useEffect, useState } from 'react'
import { Breadcrumbs } from '@/components/ui/breadcrumb'
import { Dialog } from '@/components/ui/dialog'
import { SortDescriptor } from '@progress/kendo-data-query'
import { GridColumn as Column, Grid, GridSortChangeEvent } from '@progress/kendo-react-grid'
import { PageData } from 'api/Pagination/Types'
import { WalletDto, WalletFilterKey } from 'api/Wallet/Types/Wallet'
import { Pager } from 'components/components/Pager'
import { FilterType } from 'components/Filter'
import {
  SelectClientFilter,
  SelectClientModalOpenEvent,
} from 'components/Filter/ClientFields/SelectClientFilter'
import { SelectFiltersFacade } from 'components/Filter/ClientFields/SelectFiltersFacade'
import { getWalletFilterFields } from 'components/Filter/walletFields'
import { Spinner } from 'components/Spinner'
import { createGridState, useGridFilter, useGridState, useGridStatusState } from 'features/DataGrid'
import { isGoBack } from 'lib/react-router-dom'
import { useAppSelector } from 'store'
import { GridFilterOperatorEnum, GridFilterOperatorNameEnum } from 'types/GridFilterOperatorEnum'
import { useApiClient } from 'utils/api/ApiClient'
import { WalletsApiClient } from 'utils/api/walletsApiClient'
import { links } from 'utils/featureToggles'
import { buildQuery, buildSortQuery } from 'utils/QueryHelpers'
import { TableWithExport } from 'utils/table/withExport'
import { TableStateContext, TableStateContextData } from 'utils/TableStateContext'

import { SelectClientEvent, SelectClientTable } from '../Clients/SelectClientTable'
import { normalizeWalletsSortQuery } from './normalizer'
import walletsColumn from './walletsColumn'

import './Wallets.scss'

import {
  CompositeFilterDescriptor,
  FilterDescriptor,
  GridFilterActionState,
  selectGridFilterByFilterId,
} from 'store/grid'

const useMapStateToProps = () => ({
  gridFilterByFilterIdSelector: useAppSelector(selectGridFilterByFilterId),
})

const Wallets = (): JSX.Element => {
  const crumbs = [{ title: 'Wallets', link: links.recordsWallets.url }]
  const walletsApiClient = useApiClient(WalletsApiClient)
  const [tableData, setTableData] = useState<TableStateContextData>({ skipCount: true })

  const [wallets, setWallets] = useState<WalletDto[]>([])
  const [isClientModalOpen, setClientModalOpen] = useState<boolean>(false)
  const [clientModalFilterId, setClientModalFilterId] = useState<string>()

  const { gridStatusState, gridStatusActions } = useGridStatusState()
  const { gridState, gridActions } = useGridState()
  const { selectFilterForGoBackHistory } = useGridFilter(gridStatusActions)
  const { gridFilterByFilterIdSelector } = useMapStateToProps()

  useEffect(() => {
    ;(async () => {
      await fetchGrid(
        async (gridFilterState: GridFilterActionState): Promise<PageData<WalletDto[]>> => {
          const searchQuery = getQuery(gridFilterState.filter)
          const sortQuery = getSort(gridFilterState.sort)
          const response = await walletsApiClient.getWallets({
            search: searchQuery,
            sort: sortQuery,
            pageIndex: gridFilterState.pageIndex,
            pageSize: gridFilterState.take,
            skipCount: tableData?.skipCount !== false,
          })

          if (response?.items) {
            setWallets(response.items)
          }
          setTableData({ ...tableData, meta: response })
          return response
        }
      )
    })()
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const fetchGrid = async (
    callback: (gridFilterState: GridFilterActionState) => Promise<PageData<WalletDto[]>>
  ) => {
    gridStatusActions.setLoading(true)

    const gridFilterState = selectFilterForGoBackHistory(
      createGridState({ skip: 0 }, gridState, { isGoBack })
    )
    const response = await callback(gridFilterState)
    if (response?.totalResults >= 0) {
      const total = response.totalResults

      gridActions.setGridFilters({
        total,
      })
    }

    gridStatusActions.setLoading(false)
  }

  const getQuery = useCallback(
    (filter?: CompositeFilterDescriptor) => {
      return buildQuery(filter ?? gridState.filter) ?? null
    },
    [gridState.filter]
  )

  const getSort = useCallback(
    (sortDescriptors?: SortDescriptor[]): string | null => {
      return buildSortQuery(normalizeWalletsSortQuery(sortDescriptors ?? gridState.sort))
    },
    [gridState.sort]
  )

  const filterFetch = useCallback(
    async (params?: { query?: string | null }): Promise<void> => {
      const searchQuery = params?.query !== undefined ? params.query : getQuery()
      const sortQuery = getSort(gridState.sort)
      const pageIndex = 1
      const pageSize = gridState.pageSize ?? 10
      const take = gridState.take ?? 10
      const skip = 0

      const response = await walletsApiClient.getWallets({
        search: searchQuery,
        sort: sortQuery,
        pageIndex,
        pageSize,
        skipCount: tableData?.skipCount !== false,
      })
      const total = response.totalResults

      if (response?.items) {
        setWallets(response.items.map((x) => Object.assign({ selected: false }, x)))
      }
      gridActions.setGridFilters({
        take,
        skip,
        total,
        pageIndex,
      })
      setTableData({ ...tableData, meta: response })
    },
    [getQuery, getSort, gridActions, gridState.pageSize, gridState.sort, gridState.take] // eslint-disable-line react-hooks/exhaustive-deps
  )

  const handleSelectedClients = (event: SelectClientEvent) => {
    const filter = event?.filterId ? gridFilterByFilterIdSelector(event.filterId) : null
    if (filter) {
      gridActions.addGridFiltersByFilterId({
        ...filter,
        id: event.filterId,
        value: event.value,
      })
    }
  }

  if (gridStatusState.isLoading) {
    return <Spinner />
  }

  const handleClientModalOpen = (event: SelectClientModalOpenEvent) => {
    setClientModalOpen(true)
    setClientModalFilterId(event.filterId)
  }

  const handleClientModalClose = () => {
    setClientModalOpen(false)
    setClientModalFilterId(undefined)
  }

  const handleSortChange = async (event: GridSortChangeEvent) => {
    gridActions.setGridFilters({
      sort: event.sort,
    })

    const searchQuery = getQuery(gridState.filter)
    const sortQuery = getSort(event.sort)
    const response = await walletsApiClient.getWallets({
      search: searchQuery,
      sort: sortQuery,
      pageIndex: gridState.pageIndex,
      pageSize: gridState.pageSize,
      skipCount: tableData?.skipCount !== false,
    })

    if (response?.items) {
      setWallets(response.items)
    }
    setTableData({ ...tableData, meta: response })
  }

  const handleSkipCountChange = async (skipCount: boolean) => {
    const searchQuery = getQuery()
    const sortQuery = getSort(gridState.sort)

    const response = await walletsApiClient.getWallets({
      search: searchQuery,
      sort: sortQuery,
      pageIndex: gridState.pageIndex,
      pageSize: gridState.take,
      skipCount,
    })
    const total = response.totalResults

    if (response?.items) {
      setWallets(response.items)
    }
    gridActions.setGridFilters({
      total,
    })
    setTableData({ ...tableData, skipCount, meta: response })
  }

  const handleGridPageChange = async (page: number) => {
    const searchQuery = getQuery()
    const sortQuery = getSort(gridState.sort)
    const pageIndex = page

    gridActions.setGridFilters({
      pageIndex,
    })
    const response = await walletsApiClient.getWallets({
      search: searchQuery,
      sort: sortQuery,
      pageIndex,
      pageSize: gridState.take,
      skipCount: tableData?.skipCount !== false,
    })
    const total = response.totalResults

    gridActions.setGridFilters({
      total,
    })
    if (response?.items) {
      setWallets(response.items)
    }
    setTableData({ ...tableData, meta: response })
  }

  const handlePageSizeChange = async (pageSize: number) => {
    await gridActions.setGridFilters({
      ...gridState,
      pageSize,
    })

    const searchQuery = getQuery()
    const sortQuery = getSort(gridState.sort)

    const response = await walletsApiClient.getWallets({
      search: searchQuery,
      sort: sortQuery,
      pageIndex: 1,
      pageSize,
      skipCount: tableData?.skipCount,
    })
    await gridActions.setGridFilters({
      ...gridState,
      pageIndex: 1,
      take: pageSize,
      total: response.totalResults,
    })
    if (response?.items) {
      setWallets(response.items)
    }
    setTableData({ ...tableData, meta: response })
  }

  return (
    <>
      {isClientModalOpen && (
        <Dialog open={isClientModalOpen} onOpenChange={(open) => !open && handleClientModalClose()}>
          <SelectClientTable
            filterId={clientModalFilterId}
            onSelect={handleSelectedClients}
            onClose={handleClientModalClose}
          />
        </Dialog>
      )}
      <Breadcrumbs crumbs={crumbs} />
      <SelectFiltersFacade
        gridState={gridState}
        gridStatusActions={gridStatusActions}
        gridStatusState={gridStatusState}
        setGridFilters={gridActions.setGridFilters}
        filterFetch={filterFetch}
        filterType={FilterType.WALLET}
        filterFields={[
          {
            name: WalletFilterKey.client,
            label: 'Client',
            filter: (clientFilter: {
              filter: FilterDescriptor
              onFilterChange: (event: { nextFilter: FilterDescriptor }) => void
            }) => (
              <SelectClientFilter {...clientFilter} onClientModalOpen={handleClientModalOpen} />
            ),
            operators: [
              {
                text: GridFilterOperatorNameEnum.equalOperator,
                operator: GridFilterOperatorEnum.equalOperator,
              },
              {
                text: GridFilterOperatorNameEnum.notEqOperator,
                operator: GridFilterOperatorEnum.notEqOperator,
              },
            ],
          },
          ...getWalletFilterFields(),
        ]}
        containerClassName='wallet-table__filter'
        getQuery={getQuery}
      />
      <TableStateContext.Provider value={[tableData, setTableData]}>
        <TableWithExport data={wallets} columns={walletsColumn}>
          <Grid
            {...gridState}
            resizable
            data={wallets}
            sort={gridState.sort}
            total={gridState.total}
            take={gridState.take}
            skip={gridState.skip}
            onSortChange={handleSortChange}
            pageable
            pager={() => (
              <Pager
                onSkipCountChange={handleSkipCountChange}
                onPageChange={handleGridPageChange}
                onPageSizeChange={handlePageSizeChange}
                page={gridState.pageIndex}
                pageSize={gridState.pageSize}
                take={gridState.take || 10}
                skip={gridState.skip || 0}
              />
            )}
            style={{ zIndex: 0 }}
          >
            {walletsColumn
              .filter((c) => c.show)
              .map((column, idx) => (
                <Column key={idx + 2} {...column} />
              ))}
          </Grid>
        </TableWithExport>
      </TableStateContext.Provider>
    </>
  )
}

export default Wallets
